import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminSchoolYearService } from '../../../services/admin/admin-school-year.service';
import { AdminSchoolService } from '../../../services/admin/admin-school.service';
import { SchoolYearModel } from '../../../models/school/school-year.model';
import { SchoolModel, SchoolFilter } from '../../../models/school/school.model';
import { CountryModel } from '../../../models/localization-and-pricing/country.model';
import { ModalService } from '../../../shared/modals/modal/modal.service';

@Component({
  selector: 'app-admin-school-year-associate',
  templateUrl: './admin-school-year-associate.component.html',
  styleUrls: ['./admin-school-year-associate.component.css']
})
export class AdminSchoolYearAssociateComponent implements OnInit {

  step = 1;
  schoolYear: SchoolYearModel = null;
  schoolYearId: string;

  allSchools: SchoolModel[] = [];
  selected = new Set<string>();
  private selectedSchoolsById = new Map<string, SchoolModel>();

  filterSearch = '';

  isLoadingYear = false;
  isLoadingSchools = false;
  isLoadingMore = false;
  isAssociating = false;
  hasMore = false;

  private readonly PAGE_SIZE = 50;
  private currentSkip = 0;
  private searchDebounceTimer: any;
  private alreadyAssociatedSchoolIds: string[] = [];

  associateResult: { associated: number, skipped: number, failed: number } = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private schoolYearSv: AdminSchoolYearService,
    private schoolSv: AdminSchoolService,
    private modalSv: ModalService
  ) {}

  ngOnInit() {
    this.schoolYearId = this.route.snapshot.params['schoolYearId'];
    this.isLoadingYear = true;
    this.schoolYearSv.getSchoolYear(this.schoolYearId).subscribe(year => {
      this.schoolYear = year;
      this.isLoadingYear = false;
      this.schoolYearSv.getSchoolYearSchools(this.schoolYearId).subscribe(schools => {
        this.alreadyAssociatedSchoolIds = schools.map(s => s._id);
        this.loadSchools();
      }, () => {
        this.loadSchools();
      });
    }, () => {
      this.isLoadingYear = false;
      this.modalSv.showAlertModal('Error', 'Could not load the school year. Please go back and try again.');
    });
  }

  loadSchools() {
    if (!this.schoolYear) {
      return;
    }
    this.allSchools = [];
    this.currentSkip = 0;
    this.hasMore = false;
    this.isLoadingSchools = true;
    this.fetchPage(false);
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMore) {
      return;
    }
    this.isLoadingMore = true;
    this.fetchPage(true);
  }

  onSearchChange(value: string) {
    this.filterSearch = value;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadSchools(), 300);
  }

  private fetchPage(append: boolean) {
    const filter = new SchoolFilter();
    filter.country = this.schoolYear.country;
    filter.search = this.filterSearch || undefined;
    filter.limit = this.PAGE_SIZE;
    filter.skip = this.currentSkip;
    if (this.alreadyAssociatedSchoolIds.length > 0) {
      filter.excludeSchoolIds = this.alreadyAssociatedSchoolIds.join(',');
    }
    this.schoolSv.fetchSchools(filter).subscribe(schools => {
      if (append) {
        this.allSchools = this.allSchools.concat(schools);
        this.isLoadingMore = false;
      } else {
        this.allSchools = schools;
        this.isLoadingSchools = false;
      }
      this.hasMore = schools.length === this.PAGE_SIZE;
      this.currentSkip = this.currentSkip + schools.length;
    }, () => {
      this.isLoadingSchools = false;
      this.isLoadingMore = false;
      this.modalSv.showAlertModal('Error', 'Could not load schools. Please try again.');
    });
  }

  get filteredSchools(): SchoolModel[] {
    return this.allSchools;
  }

  get selectedSchools(): SchoolModel[] {
    return Array.from(this.selectedSchoolsById.values());
  }

  get totalStudents(): number {
    return this.selectedSchools.reduce((sum, s) => sum + (s.numLicenses || 0), 0);
  }

  toggleRow(id: string) {
    if (this.selected.has(id)) {
      this.selected.delete(id);
      this.selectedSchoolsById.delete(id);
    } else {
      this.selected.add(id);
      const school = this.allSchools.find(s => s._id === id);
      if (school) {
        this.selectedSchoolsById.set(id, school);
      }
    }
  }

  isSelected(id: string): boolean {
    return this.selected.has(id);
  }

  get allVisibleSelected(): boolean {
    const vis = this.filteredSchools;
    return vis.length > 0 && vis.every(s => this.selected.has(s._id));
  }

  get someVisibleSelected(): boolean {
    return this.filteredSchools.some(s => this.selected.has(s._id));
  }

  toggleSelectAllVisible(checked: boolean) {
    this.filteredSchools.forEach(s => {
      if (checked) {
        this.selected.add(s._id);
        this.selectedSchoolsById.set(s._id, s);
      } else {
        this.selected.delete(s._id);
        this.selectedSchoolsById.delete(s._id);
      }
    });
  }

  selectAllCountry() {
    this.allSchools
      .filter(s => !this.schoolYear || s.country === this.schoolYear.country)
      .forEach(s => {
        this.selected.add(s._id);
        this.selectedSchoolsById.set(s._id, s);
      });
  }

  clearSelection() {
    this.selected.clear();
    this.selectedSchoolsById.clear();
  }

  goToConfirm() {
    if (!this.selected.size) {
      return;
    }
    this.step = 2;
  }

  backToSelect() {
    this.step = 1;
  }

  confirmAssociate() {
    this.isAssociating = true;
    const ids = Array.from(this.selected);
    this.schoolYearSv.bulkAssociateSchoolYear(ids, this.schoolYearId).subscribe(result => {
      this.isAssociating = false;
      if (result.associated === 0 && result.skipped === 0 && result.failed > 0) {
        this.modalSv.showAlertModal(
          'Association failed',
          'None of the selected schools could be associated. Please try again.'
        );
        return;
      }
      this.associateResult = result;
      this.step = 3;
    }, () => {
      this.isAssociating = false;
      this.modalSv.showAlertModal(
        'Association failed',
        'Something went wrong while associating schools. Please try again.'
      );
    });
  }

  backToDetail() {
    this.router.navigate(['admin', 'schoolYears', this.schoolYearId, 'detail']);
  }

  associateMore() {
    this.selected.clear();
    this.selectedSchoolsById.clear();
    this.associateResult = null;
    this.step = 1;
  }

  countryTitle(code: string): string {
    return CountryModel.title(code);
  }

  countryIconUrl(code: string): string {
    return CountryModel.iconURL(code);
  }

  schoolInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name.substring(0, 2).toUpperCase();
  }
}
